"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Loader2, RefreshCw } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { useLocation } from "@/contexts/location-context";
import { fetchCountryCities, type CountryCity } from "@/lib/country-cities";
import { cn } from "@/lib/utils";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

export function LocationDialog() {
    const {
        coordinates,
        placeName,
        detectedCountry,
        error,
        loading,
        source,
        requestPermission,
        setManualLocation,
        applyIpFallbackLocation,
        hasLocation,
        needsCitySelection,
        locationDialogOpen,
        setLocationDialogOpen,
    } = useLocation();

    const [citySearch, setCitySearch] = useState("");
    const [searchResults, setSearchResults] = useState<CountryCity[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showCityOverride, setShowCityOverride] = useState(false);

    const showNeedsAttention = needsCitySelection || error !== null;
    const showCityPicker = source !== "geolocation" || showCityOverride;
    const countryCode = detectedCountry?.countryCode ?? "nz";
    const countryName = detectedCountry?.country ?? "your country";
    const trimmedSearch = citySearch.trim();
    const canSearch = trimmedSearch.length >= MIN_SEARCH_LENGTH;

    useEffect(() => {
        if (!locationDialogOpen || !showCityPicker) {
            return;
        }

        if (!canSearch) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        let cancelled = false;
        setIsSearching(true);

        const timeoutId = setTimeout(() => {
            void fetchCountryCities(countryCode, trimmedSearch).then((results) => {
                if (cancelled) return;
                setSearchResults(results);
                setIsSearching(false);
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [locationDialogOpen, showCityPicker, countryCode, trimmedSearch, canSearch]);

    const handleCitySelect = (city: CountryCity) => {
        setManualLocation(city.lat, city.lon, city.name);
        setCitySearch("");
        setSearchResults([]);
        setLocationDialogOpen(false);
    };

    const handleOpenChange = (open: boolean) => {
        if (!open && needsCitySelection) {
            void applyIpFallbackLocation();
        }
        setLocationDialogOpen(open);
        if (!open) {
            setCitySearch("");
            setSearchResults([]);
            setShowCityOverride(false);
        }
    };

    const showResultsList = canSearch || isSearching || searchResults.length > 0;

    return (
        <Dialog open={locationDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        "ml-1 p-1 rounded text-muted-foreground cursor-pointer hover:opacity-80 transition-opacity",
                        showNeedsAttention && "text-amber-500",
                    )}
                    aria-label="Location settings"
                >
                    <MapPin className="size-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Location</DialogTitle>
                    <DialogDescription>
                        {needsCitySelection
                            ? `Choose your city for accurate aurora forecasts. We detected ${countryName} from your connection.`
                            : "Your location is used for forecasts and map display."}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" />
                            <span>Getting your location...</span>
                        </div>
                    ) : source === "geolocation" && coordinates && !showCityOverride ? (
                        <div className="flex flex-col gap-2">
                            <p className="text-xs font-medium text-muted-foreground">Current location</p>
                            {placeName && <p className="text-sm font-medium">{placeName}</p>}
                            <p className="text-xs text-muted-foreground">Using GPS</p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowCityOverride(true)}
                                className="w-fit"
                            >
                                Choose a different city
                            </Button>
                        </div>
                    ) : null}

                    {showCityPicker && !loading && (
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="city-search">Select city</Label>
                            <Command shouldFilter={false} className="rounded-md border">
                                <CommandInput
                                    id="city-search"
                                    placeholder={`Search cities in ${countryName}...`}
                                    value={citySearch}
                                    onValueChange={setCitySearch}
                                />
                                {showResultsList && (
                                    <CommandList>
                                        {isSearching && searchResults.length === 0 && (
                                            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                                                <Loader2 className="size-4 animate-spin" />
                                                <span>Searching...</span>
                                            </div>
                                        )}
                                        {canSearch && !isSearching && searchResults.length === 0 && (
                                            <CommandEmpty>No city found.</CommandEmpty>
                                        )}
                                        <CommandGroup>
                                            {searchResults.map((city) => (
                                                <CommandItem
                                                    key={city.id}
                                                    value={city.name}
                                                    onSelect={() => handleCitySelect(city)}
                                                >
                                                    {city.name}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                )}
                            </Command>
                            {needsCitySelection && (
                                <p className="text-xs text-muted-foreground">
                                    IP location is only accurate to the country. Select your city, or close to use the best approximate location we can detect.
                                </p>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={requestPermission}
                                className="w-fit"
                            >
                                <RefreshCw data-icon="inline-start" />
                                Use GPS instead
                            </Button>
                            {error && <p className="text-sm text-destructive">{error}</p>}
                        </div>
                    )}

                    {hasLocation && source === "manual" && placeName && (
                        <div className="flex flex-col gap-1 border-t pt-4">
                            <p className="text-xs font-medium text-muted-foreground">Forecast location</p>
                            <p className="text-sm font-medium">{placeName}</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
